import PathConstants from '@/main/routes/path-contants'
import theme from '@/presentation/assets/styles/theme'
import { Box, Container, Tabs } from '@mui/material'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { ValidAccountState } from '@/presentation/contexts/auth'
import useAccount from '@/presentation/hooks/auth/use-account'
import { TabApp } from '../../../navigation/tab/tab-app'
import { TabMenuApp } from '../../../navigation/tab/tab-menu-app'
import { Analytics } from '../../analytics'

import HomeIcon from '@mui/icons-material/Home'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import ProfileAssessmentButton from '../../../button/profile-assessment-button'

export default function DesktopNavMenuMain() {
  const [account] = useAccount() as ValidAccountState
  const ldClient = useLDClient()
  const routes = PathConstants.Routes
  const { HOME, INSTITUTIONS } = routes
  const restrictions = account.company.contentRestrictions.categories
  const enrollmentCategoryList = EnrollmentCategory.getAvailables(restrictions)
  const location = useLocation()
  const [value, setValue] = useState<string | boolean>(false)

  const findSelectedEnrollmentCategory = (pathname: string) => {
    return enrollmentCategoryList.find(ec =>
      ec
        .getAvailableCategories(restrictions)
        .some(category => PathConstants.findRouteByCategory(category).value === pathname)
    )
  }

  const identifySelectedValue = () => {
    switch (location.pathname) {
      case HOME.value:
        return HOME.name
      case INSTITUTIONS.value:
        return INSTITUTIONS.name
      default:
        return findSelectedEnrollmentCategory(location.pathname)?.name
    }
  }

  useEffect(() => {
    const selectedValue = identifySelectedValue()
    setValue(selectedValue ?? false)
  }, [location])

  const handleChange = (_: React.SyntheticEvent, newValue: string | boolean) => {
    const enrollmentCategory = enrollmentCategoryList.find(ec => ec.name === newValue)
    if (enrollmentCategory?.hasMultipleCategories) {
      Analytics.trackOpenOptionMenu(newValue)
    } else {
      Analytics.trackNavigate(newValue)
    }
  }

  const tabsSx = {
    minHeight: theme.spacing(8),
    height: theme.spacing(8)
  }

  const enrollmentCategoriesTabs = enrollmentCategoryList.map(ec => {
    return ec.hasMultipleCategories ? (
      <TabMenuApp
        key={ec.name}
        label={ec.title}
        value={ec.name}
        selectedValue={value}
        options={ec.categories.map(c => {
          return {
            label: c.title,
            to: PathConstants.findRouteByCategory(c).value,
            value: c.name
          }
        })}
        sx={tabsSx}
      />
    ) : (
      <TabApp
        key={ec.name}
        component={Link}
        label={ec.title}
        to={PathConstants.getCategoryRoutePath(ec.categories[0])}
        value={ec.name}
        selectedValue={value}
        sx={tabsSx}
      />
    )
  })

  return (
    <Container maxWidth="lg" disableGutters>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="standard"
          allowScrollButtonsMobile
          aria-label="scrollable force tabs example"
          sx={{
            height: theme.spacing('auto')
          }}
        >
          {[
            <TabApp
              key={HOME.name}
              component={Link}
              icon={<HomeIcon fontSize="small" />}
              to={HOME.value}
              value={HOME.name}
              selectedValue={value}
              sx={{
                ...tabsSx,
                '.MuiTab-iconWrapper': {
                  marginBottom: 0
                }
              }}
            />,
            ...enrollmentCategoriesTabs,
            <TabApp
              key={INSTITUTIONS.name}
              component={Link}
              label={INSTITUTIONS.title}
              to={INSTITUTIONS.value}
              value={INSTITUTIONS.name}
              selectedValue={value}
              sx={tabsSx}
            />
          ]}
        </Tabs>

        <Box>
          {FeatureFlag.Key.COMPANIES_WITH_PROFILE_ASSESSMENT.isEnabled(ldClient) && (
            <ProfileAssessmentButton mode="desktop" />
          )}
        </Box>
      </Box>
    </Container>
  )
}
